// ═══════════════════════════════════════════════════════════════════════════════
// Admin — system message broadcast CRUD (super_admin only)
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from 'react';
import {
  ADMIN_ROLE,
  SYSTEM_MESSAGE_MATCH_MODE,
  USER_LEVEL,
  USER_ROLE,
  type SystemMessageAdmin,
  type SystemMessageAudienceEstimate,
  type SystemMessageMatchMode,
  type UserRole,
} from '@fdh/shared';
import { Navigate } from 'react-router-dom';
import { HtmlEditor } from '../../components/admin/HtmlEditor';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { adminApi } from '../../lib/adminApi';
import {
  ADMIN_HKT_DATETIME_PLACEHOLDER,
  hktWallToUtcWall,
  utcWallToHktWall,
} from '../../lib/adminHktDatetime';
import { adminPortalPath } from '../../lib/adminPortalPaths';

type FormState = {
  isActive: boolean;
  subjectZhHant: string;
  subjectEn: string;
  detailZhHant: string;
  detailEn: string;
  displayFrom: string;
  displayTo: string;
  targetRoles: UserRole[];
  targetLevels: number[];
  targetMatchMode: SystemMessageMatchMode;
  showOnEmployerDashboard: boolean;
  showOnHelperHome: boolean;
  tickerBgColor: string;
  notifyInApp: boolean;
  notifyWebPush: boolean;
  notifyEmail: boolean;
};

const emptyForm = (): FormState => ({
  isActive: true,
  subjectZhHant: '',
  subjectEn: '',
  detailZhHant: '',
  detailEn: '',
  displayFrom: '',
  displayTo: '',
  targetRoles: [],
  targetLevels: [],
  targetMatchMode: SYSTEM_MESSAGE_MATCH_MODE.AND,
  showOnEmployerDashboard: true,
  showOnHelperHome: true,
  tickerBgColor: '#FEF3C7',
  notifyInApp: false,
  notifyWebPush: false,
  notifyEmail: false,
});

const toForm = (row: SystemMessageAdmin): FormState => ({
  isActive: row.isActive,
  subjectZhHant: row.subjectZhHant ?? '',
  subjectEn: row.subjectEn ?? '',
  detailZhHant: row.detailZhHant ?? '',
  detailEn: row.detailEn ?? '',
  displayFrom: utcWallToHktWall(row.displayFrom ?? ''),
  displayTo: utcWallToHktWall(row.displayTo ?? ''),
  targetRoles: row.targetRoles,
  targetLevels: row.targetLevels,
  targetMatchMode: row.targetMatchMode,
  showOnEmployerDashboard: row.showOnEmployerDashboard,
  showOnHelperHome: row.showOnHelperHome,
  tickerBgColor: row.tickerBgColor,
  notifyInApp: row.notifyInApp,
  notifyWebPush: row.notifyWebPush,
  notifyEmail: row.notifyEmail,
});

const toPayload = (form: FormState) => ({
  isActive: form.isActive,
  subjectZhHant: form.subjectZhHant.trim() || undefined,
  subjectEn: form.subjectEn.trim() || undefined,
  detailZhHant: form.detailZhHant.trim() || undefined,
  detailEn: form.detailEn.trim() || undefined,
  displayFrom:
    form.displayFrom.trim() === '' ? null : hktWallToUtcWall(form.displayFrom.trim()),
  displayTo: form.displayTo.trim() === '' ? null : hktWallToUtcWall(form.displayTo.trim()),
  targetRoles: form.targetRoles,
  targetLevels: form.targetLevels,
  targetMatchMode: form.targetMatchMode,
  showOnEmployerDashboard: form.showOnEmployerDashboard,
  showOnHelperHome: form.showOnHelperHome,
  tickerBgColor: form.tickerBgColor,
  notifyInApp: form.notifyInApp,
  notifyWebPush: form.notifyWebPush,
  notifyEmail: form.notifyEmail,
});

export const AdminSystemMessagesPage = () => {
  const { t } = useLanguage();
  const { admin } = useAdminAuth();
  const [items, setItems] = useState<SystemMessageAdmin[]>([]);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [estimate, setEstimate] = useState<SystemMessageAudienceEstimate | null>(null);
  const [estimating, setEstimating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApi.get<{ items: SystemMessageAdmin[] }>('/admin/system-messages');
      if (!res.success || !res.data) {
        setError(res.error ?? t('admin.systemMessage.loadError'));
        return;
      }
      setItems(res.data.items);
    } catch {
      setError(t('admin.systemMessage.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (admin?.role !== ADMIN_ROLE.SUPER_ADMIN) {
      return;
    }
    void load();
  }, [admin?.role, load]);

  if (admin?.role !== ADMIN_ROLE.SUPER_ADMIN) {
    return <Navigate to={adminPortalPath('dashboard')} replace />;
  }

  const handleToggleRole = (role: UserRole): void => {
    setForm((prev) => ({
      ...prev,
      targetRoles: prev.targetRoles.includes(role)
        ? prev.targetRoles.filter((r) => r !== role)
        : [...prev.targetRoles, role],
    }));
  };

  const handleToggleLevel = (level: number): void => {
    setForm((prev) => ({
      ...prev,
      targetLevels: prev.targetLevels.includes(level)
        ? prev.targetLevels.filter((l) => l !== level)
        : [...prev.targetLevels, level],
    }));
  };

  const handleEdit = (row: SystemMessageAdmin): void => {
    setEditingId(row.id);
    setForm(toForm(row));
    setConfirmOpen(false);
    setEstimate(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleReset = (): void => {
    setEditingId(null);
    setForm(emptyForm());
    setConfirmOpen(false);
    setEstimate(null);
  };

  const handleOpenConfirm = async (): Promise<void> => {
    setError(null);
    if (!form.subjectZhHant.trim() && !form.subjectEn.trim()) {
      setError(t('admin.systemMessage.subjectRequired'));
      return;
    }
    if (editingId) {
      setSaving(true);
      try {
        const res = await adminApi.put(`/admin/system-messages/${editingId}`, toPayload(form));
        if (!res.success) {
          setError(res.error ?? t('admin.systemMessage.saveError'));
          return;
        }
        handleReset();
        await load();
      } catch {
        setError(t('admin.systemMessage.saveError'));
      } finally {
        setSaving(false);
      }
      return;
    }

    setEstimating(true);
    try {
      const res = await adminApi.post<SystemMessageAudienceEstimate>(
        '/admin/system-messages/estimate-audience',
        toPayload(form),
      );
      if (!res.success || !res.data) {
        setError(res.error ?? t('admin.systemMessage.estimateError'));
        return;
      }
      setEstimate(res.data);
      setConfirmOpen(true);
    } catch {
      setError(t('admin.systemMessage.estimateError'));
    } finally {
      setEstimating(false);
    }
  };

  const handleConfirmCreate = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      const res = await adminApi.post('/admin/system-messages', {
        ...toPayload(form),
        confirmSend: true,
      });
      if (!res.success) {
        setError(res.error ?? t('admin.systemMessage.saveError'));
        return;
      }
      setConfirmOpen(false);
      handleReset();
      await load();
    } catch {
      setError(t('admin.systemMessage.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const showMatchMode = form.targetRoles.length > 0 && form.targetLevels.length > 0;

  return (
    <div className="mx-auto max-w-5xl space-y-6 px-4 py-6">
      <div>
        <h1 className="text-xl font-bold text-white">{t('admin.systemMessage.title')}</h1>
        <p className="mt-1 text-sm text-slate-400">{t('admin.systemMessage.subtitle')}</p>
      </div>

      {error ? (
        <div className="rounded-md border border-red-500/40 bg-red-950/40 px-3 py-2 text-sm text-red-200" role="alert">
          {error}
        </div>
      ) : null}

      <section className="rounded-xl border border-slate-800 bg-slate-900/80 p-4">
        <h2 className="mb-3 text-sm font-semibold text-amber-300">
          {editingId ? t('admin.systemMessage.editTitle') : t('admin.systemMessage.createTitle')}
        </h2>
        <div className="grid gap-3 md:grid-cols-2">
          <label className="block text-xs text-slate-300">
            {t('admin.systemMessage.subjectZhHant')}
            <input
              type="text"
              maxLength={100}
              value={form.subjectZhHant}
              onChange={(e) => setForm((prev) => ({ ...prev, subjectZhHant: e.target.value }))}
              className="mt-1 w-full rounded-md border border-slate-600 bg-slate-950 px-3 py-2 text-sm text-slate-100"
            />
          </label>
          <label className="block text-xs text-slate-300">
            {t('admin.systemMessage.subjectEn')}
            <input
              type="text"
              maxLength={100}
              value={form.subjectEn}
              onChange={(e) => setForm((prev) => ({ ...prev, subjectEn: e.target.value }))}
              className="mt-1 w-full rounded-md border border-slate-600 bg-slate-950 px-3 py-2 text-sm text-slate-100"
            />
          </label>
          <HtmlEditor
            id="detail-zh-hant"
            label={t('admin.systemMessage.detailZhHant')}
            value={form.detailZhHant}
            onChange={(html) => setForm((prev) => ({ ...prev, detailZhHant: html }))}
          />
          <HtmlEditor
            id="detail-en"
            label={t('admin.systemMessage.detailEn')}
            value={form.detailEn}
            onChange={(html) => setForm((prev) => ({ ...prev, detailEn: html }))}
          />
          <label className="block text-xs text-slate-300">
            {t('admin.systemMessage.displayFrom')}
            <input
              type="text"
              placeholder={ADMIN_HKT_DATETIME_PLACEHOLDER}
              value={form.displayFrom}
              onChange={(e) => setForm((prev) => ({ ...prev, displayFrom: e.target.value }))}
              className="mt-1 w-full rounded-md border border-slate-600 bg-slate-950 px-3 py-2 text-sm text-slate-100"
              aria-label={t('admin.systemMessage.displayFrom')}
            />
          </label>
          <label className="block text-xs text-slate-300">
            {t('admin.systemMessage.displayTo')}
            <input
              type="text"
              placeholder={ADMIN_HKT_DATETIME_PLACEHOLDER}
              value={form.displayTo}
              onChange={(e) => setForm((prev) => ({ ...prev, displayTo: e.target.value }))}
              className="mt-1 w-full rounded-md border border-slate-600 bg-slate-950 px-3 py-2 text-sm text-slate-100"
              aria-label={t('admin.systemMessage.displayTo')}
            />
          </label>
          <label className="block text-xs text-slate-300">
            {t('admin.systemMessage.tickerBgColor')}
            <input
              type="color"
              value={form.tickerBgColor}
              onChange={(e) => setForm((prev) => ({ ...prev, tickerBgColor: e.target.value.toUpperCase() }))}
              className="mt-1 h-10 w-full cursor-pointer rounded-md border border-slate-600 bg-slate-950"
              aria-label={t('admin.systemMessage.tickerBgColor')}
            />
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-200">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
            />
            {t('admin.systemMessage.active')}
          </label>
        </div>

        <fieldset className="mt-4">
          <legend className="text-xs font-medium text-slate-300">
            {t('admin.systemMessage.showOnHome')}
          </legend>
          <p className="mt-0.5 text-xs text-slate-500">{t('admin.systemMessage.showOnHomeHint')}</p>
          <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-200">
            <label className="inline-flex items-center gap-1.5">
              <input
                type="checkbox"
                checked={form.showOnEmployerDashboard}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, showOnEmployerDashboard: e.target.checked }))
                }
              />
              {t('admin.systemMessage.showOnEmployerDashboard')}
            </label>
            <label className="inline-flex items-center gap-1.5">
              <input
                type="checkbox"
                checked={form.showOnHelperHome}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, showOnHelperHome: e.target.checked }))
                }
              />
              {t('admin.systemMessage.showOnHelperHome')}
            </label>
          </div>
        </fieldset>

        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <fieldset>
            <legend className="text-xs font-medium text-slate-300">{t('admin.systemMessage.roles')}</legend>
            <div className="mt-1 flex flex-wrap gap-3 text-sm text-slate-200">
              <label className="inline-flex items-center gap-1.5">
                <input
                  type="checkbox"
                  checked={form.targetRoles.includes(USER_ROLE.EMPLOYER)}
                  onChange={() => handleToggleRole(USER_ROLE.EMPLOYER)}
                />
                Employer
              </label>
              <label className="inline-flex items-center gap-1.5">
                <input
                  type="checkbox"
                  checked={form.targetRoles.includes(USER_ROLE.HELPER)}
                  onChange={() => handleToggleRole(USER_ROLE.HELPER)}
                />
                Helper
              </label>
              <span className="text-xs text-slate-500">{t('admin.systemMessage.blankMeansAll')}</span>
            </div>
          </fieldset>
          <fieldset>
            <legend className="text-xs font-medium text-slate-300">{t('admin.systemMessage.levels')}</legend>
            <div className="mt-1 flex flex-wrap gap-3 text-sm text-slate-200">
              {[USER_LEVEL.TRIAL, USER_LEVEL.FREE, USER_LEVEL.PAID].map((level) => (
                <label key={level} className="inline-flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={form.targetLevels.includes(level)}
                    onChange={() => handleToggleLevel(level)}
                  />
                  L{level}
                </label>
              ))}
              <span className="text-xs text-slate-500">{t('admin.systemMessage.blankMeansAll')}</span>
            </div>
          </fieldset>
        </div>

        {showMatchMode ? (
          <fieldset className="mt-3">
            <legend className="text-xs font-medium text-slate-300">{t('admin.systemMessage.matchMode')}</legend>
            <div className="mt-1 flex gap-4 text-sm text-slate-200">
              <label className="inline-flex items-center gap-1.5">
                <input
                  type="radio"
                  name="matchMode"
                  checked={form.targetMatchMode === SYSTEM_MESSAGE_MATCH_MODE.AND}
                  onChange={() =>
                    setForm((prev) => ({ ...prev, targetMatchMode: SYSTEM_MESSAGE_MATCH_MODE.AND }))
                  }
                />
                AND
              </label>
              <label className="inline-flex items-center gap-1.5">
                <input
                  type="radio"
                  name="matchMode"
                  checked={form.targetMatchMode === SYSTEM_MESSAGE_MATCH_MODE.OR}
                  onChange={() =>
                    setForm((prev) => ({ ...prev, targetMatchMode: SYSTEM_MESSAGE_MATCH_MODE.OR }))
                  }
                />
                OR
              </label>
            </div>
          </fieldset>
        ) : null}

        <fieldset className="mt-4">
          <legend className="text-xs font-medium text-slate-300">{t('admin.systemMessage.channels')}</legend>
          <div className="mt-1 flex flex-wrap gap-4 text-sm text-slate-200">
            <label className="inline-flex items-center gap-1.5">
              <input
                type="checkbox"
                checked={form.notifyInApp}
                onChange={(e) => setForm((prev) => ({ ...prev, notifyInApp: e.target.checked }))}
                disabled={Boolean(editingId)}
              />
              In-app
            </label>
            <label className="inline-flex items-center gap-1.5">
              <input
                type="checkbox"
                checked={form.notifyWebPush}
                onChange={(e) => setForm((prev) => ({ ...prev, notifyWebPush: e.target.checked }))}
                disabled={Boolean(editingId)}
              />
              Web push
            </label>
            <label className="inline-flex items-center gap-1.5">
              <input
                type="checkbox"
                checked={form.notifyEmail}
                onChange={(e) => setForm((prev) => ({ ...prev, notifyEmail: e.target.checked }))}
                disabled={Boolean(editingId)}
              />
              Email
            </label>
          </div>
          {editingId ? (
            <p className="mt-1 text-xs text-slate-500">{t('admin.systemMessage.editNoResend')}</p>
          ) : null}
        </fieldset>

        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => void handleOpenConfirm()}
            disabled={saving || estimating}
            className="rounded-md bg-amber-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-50"
          >
            {editingId ? t('admin.systemMessage.saveEdit') : t('admin.systemMessage.saveCreate')}
          </button>
          {editingId ? (
            <button
              type="button"
              onClick={handleReset}
              className="rounded-md border border-slate-600 px-4 py-2 text-sm text-slate-200 hover:bg-slate-800"
            >
              {t('admin.systemMessage.cancelEdit')}
            </button>
          ) : null}
        </div>
      </section>

      <section className="rounded-xl border border-slate-800 bg-slate-900/80 p-4">
        <h2 className="mb-3 text-sm font-semibold text-amber-300">{t('admin.systemMessage.listTitle')}</h2>
        {loading ? (
          <p className="text-sm text-slate-400">{t('common.loading')}</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-slate-400">{t('admin.systemMessage.listEmpty')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm text-slate-200">
              <thead className="text-xs uppercase text-slate-400">
                <tr>
                  <th className="px-2 py-2">{t('admin.systemMessage.colSubject')}</th>
                  <th className="px-2 py-2">{t('admin.systemMessage.colActive')}</th>
                  <th className="px-2 py-2">{t('admin.systemMessage.colNotify')}</th>
                  <th className="px-2 py-2">{t('admin.systemMessage.colFrom')}</th>
                  <th className="px-2 py-2" />
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.id} className="border-t border-slate-800">
                    <td className="px-2 py-2">
                      {row.subjectEn || row.subjectZhHant || row.id.slice(0, 8)}
                    </td>
                    <td className="px-2 py-2">{row.isActive ? 'Yes' : 'No'}</td>
                    <td className="px-2 py-2">{row.notificationStatus}</td>
                    <td className="px-2 py-2 whitespace-nowrap">
                      {row.displayFrom ? utcWallToHktWall(row.displayFrom) : '—'}
                    </td>
                    <td className="px-2 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => handleEdit(row)}
                        className="text-amber-300 underline hover:text-amber-200"
                      >
                        {t('admin.systemMessage.edit')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {confirmOpen && estimate ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
          role="presentation"
          onClick={() => setConfirmOpen(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="system-message-confirm-title"
            className="w-full max-w-md rounded-xl border border-slate-700 bg-slate-900 p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="system-message-confirm-title" className="text-lg font-semibold text-white">
              {t('admin.systemMessage.confirmTitle')}
            </h3>
            <ul className="mt-3 space-y-1 text-sm text-slate-300">
              <li>
                {t('admin.systemMessage.confirmTotal', { count: estimate.totalMatchingUsers })}
              </li>
              <li>{t('admin.systemMessage.confirmInApp', { count: estimate.inAppCount })}</li>
              <li>
                {t('admin.systemMessage.confirmPush', { count: estimate.webPushEligibleCount })}
              </li>
              <li>
                {t('admin.systemMessage.confirmEmail', { count: estimate.emailEligibleCount })}
              </li>
              <li>
                {t('admin.systemMessage.confirmWhen', {
                  when: estimate.willNotifyAt
                    ? utcWallToHktWall(estimate.willNotifyAt)
                    : t('admin.systemMessage.confirmWhenNone'),
                })}
              </li>
            </ul>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                className="rounded-md border border-slate-600 px-3 py-1.5 text-sm text-slate-200"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={() => void handleConfirmCreate()}
                disabled={saving}
                className="rounded-md bg-amber-500 px-3 py-1.5 text-sm font-semibold text-slate-950 disabled:opacity-50"
              >
                {t('admin.systemMessage.confirmSend')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};
